import {useMemo} from 'react';
import {XCircleIcon} from '@heroicons/react/24/outline';
import Switch from '../../../../../components/Switch';
import Input from '../../../../../components/_form/Input';
import {currencyFormat} from '../../../../../utils/currency';

const defaultItem = () => ({
  id: null,
  name: '',
  notes: '',
  quantity: 1,
  unitPrice: 0,
  laborIncluded: false,
  laborPrice: 0,
  taxable: false,
});

const ItemRow = ({
  item,
  onChange = () => {},
  onDelete = () => {},
  onToggleLabor = () => {},
}) => {
  const profitAmount = useMemo(() => {
    if (!item.profit || item.profit === 0) return 0;

    const qty = Number(item.quantity) || 0;
    const unit = Number(item.unitPrice) || 0;
    const labor = item.laborIncluded ? Number(item.laborPrice) || 0 : 0;
    const amount = qty * unit + labor;
    return (amount * Number(item.profit)) / 100;
  }, [
    item.quantity,
    item.unitPrice,
    item.laborIncluded,
    item.laborPrice,
    item.profitAmount,
    item.profit,
  ]);

  const unitTotal = useMemo(() => {
    const qty = Number(item.quantity) || 0;
    const unit = Number(item.unitPrice) || 0;
    const labor = item.laborIncluded ? Number(item.laborPrice) || 0 : 0;
    const profit = profitAmount;
    return qty * unit + (labor + profit);
  }, [
    item.quantity,
    item.unitPrice,
    item.laborIncluded,
    item.laborPrice,
    item.profitAmount,
    profitAmount,
  ]);

  return (
    <div className="flex items-center gap-2">
      <div className="grid grid-cols-12 gap-2 py-2 px-4 border items-start justify-between rounded-lg">
        <div className="col-span-4">
          <Input
            label="Description"
            value={item.name}
            onChange={(e) => onChange({...item, name: e.target.value})}
            placeholder="Item description"
          />
        </div>

        <div className="col-span-1">
          <Input
            label="Qty"
            type="number"
            value={item.quantity}
            min={0}
            onChange={(e) =>
              onChange({...item, quantity: Number(e.target.value) || 0})
            }
          />
        </div>

        <div className="col-span-2">
          <Input
            label="Unit Price"
            type="number"
            value={item.unitPrice}
            onChange={(e) =>
              onChange({
                ...item,
                unitPrice: Number(e.target.value) || 0,
              })
            }
          />
        </div>

        {item.laborPrice > 0 && (
          <div className="col-span-1">
            <div className="flex items-center justify-start gap-2">
              <span className="text-xs font-medium">Labor</span>
              <Switch checked={item.laborIncluded} onChange={onToggleLabor} />
            </div>
            {item.laborIncluded && (
              <Input
                label=""
                type="number"
                value={item.laborPrice}
                onChange={(e) =>
                  onChange({
                    ...item,
                    laborPrice: Number(e.target.value) || 0,
                  })
                }
              />
            )}
          </div>
        )}

        {item.profitAmount > 0 && (
          <div className="col-span-1">
            {item.laborIncluded && (
              <Input
                label="Profit %"
                type="number"
                value={item.profit}
                onChange={(e) =>
                  onChange({
                    ...item,
                    profit: Number(e.target.value) || 0,
                  })
                }
              />
            )}
          </div>
        )}

        <div className={item.laborPrice > 0 ? 'col-span-2' : 'col-span-4'}>
          <Input
            label="Notes"
            value={item.notes}
            onChange={(e) => onChange({...item, notes: e.target.value})}
            placeholder="Optional notes"
          />
        </div>

        <div className="col-span-1">
          {item.profitAmount > 0 && (
            <div className="flex flex-col items-end justify-between">
              <span className="text-xs font-medium">Profit</span>
              <span className="text-xs text-gray-500">
                {currencyFormat(profitAmount)}
              </span>
            </div>
          )}
          <div className="flex flex-col items-end justify-between">
            <span className="text-xs font-medium">Total</span>
            <span className="text-xs text-gray-500">
              {currencyFormat(unitTotal)}
            </span>
          </div>
          <div className="flex flex-col items-end justify-start gap-1 mt-2">
            <span className="text-xs">Taxable</span>
            <Switch
              checked={item.taxable}
              onChange={(val) => onChange({...item, taxable: val})}
            />
          </div>
        </div>
      </div>
      <div className="col-span-1 flex flex-col justify-between">
        <button
          type="button"
          className="text-red-700 hover:text-red-500"
          onClick={onDelete}>
          <XCircleIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export {defaultItem};
export default ItemRow;
